/**
 * What a route shows when it cannot render.
 *
 * There was no `errorElement` on the router at all, so any route-level failure
 * fell through to React Router's default: a bare "Unexpected Application Error!"
 * over a raw exception message. An operator saw
 * `TypeError: Failed to fetch dynamically imported module` — accurate, and
 * useless to the person reading it.
 *
 * Two cases, deliberately worded differently:
 *
 *  • A STALE BUILD. `lazyWithReload` already reloads once for this, so reaching
 *    here means the reload did not fix it — the tab is offline, or the deploy
 *    itself is broken. Reloading again is still the right first move, so the
 *    button does that, but the copy no longer promises it will work.
 *
 *  • ANYTHING ELSE. A real fault. Reloading is unlikely to help, so the page
 *    offers the way back to the console instead and shows the detail for a bug
 *    report rather than hiding it.
 *
 * NEVER lazy-loaded itself: an error page delivered as its own chunk fails in
 * exactly the situation it exists to report.
 */

import { useRouteError } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { WarningIcon } from '@phosphor-icons/react'
import { isChunkLoadError } from './lazyWithReload'
import DiagButton from '../components/diagnostics/DiagButton'
import styles from './RouteError.module.css'

export default function RouteError() {
  const error = useRouteError()
  const { t } = useTranslation()

  const stale = isChunkLoadError(error)
  const detail =
    error instanceof Error ? error.message : typeof error === 'string' ? error : null

  return (
    <div className={styles.root} role="alert">
      <WarningIcon size={28} weight="duotone" className={styles.icon} />

      <h1 className={styles.title}>
        {stale ? t('routeError.staleTitle') : t('routeError.title')}
      </h1>

      <p className={styles.body}>
        {stale ? t('routeError.staleBody') : t('routeError.body')}
      </p>

      <div className={styles.actions}>
        <DiagButton variant="primary" onClick={() => window.location.reload()}>
          {t('routeError.reload')}
        </DiagButton>
        {!stale && (
          <DiagButton variant="default" onClick={() => window.location.assign('/')}>
            {t('routeError.home')}
          </DiagButton>
        )}
      </div>

      {/* The message stays visible — it is what makes a bug report actionable,
          and hiding it only means it gets retyped from a phone photo. */}
      {detail != null && (
        <pre className={styles.detail} dir="ltr">
          {detail}
        </pre>
      )}
    </div>
  )
}
